<template>
    <div>
        <div class="bg-white border-bottom">
            <div class="container py-3">
                <div class="d-flex align-items-center">
                    <img
                        src="https://getasia.s3.ap-southeast-1.amazonaws.com/images/hextar_logo.png"
                        style="height: 48px; width: auto;"
                    >
                    <div class="border-left ml-3 pl-3">
                        <h5 class="font-weight-bold mb-0">Candidate Employee</h5>
                        <small class="text-muted">Create an employee account</small>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-light py-4">
            <div class="container">
                <div v-if="!status.detail" class="d-flex justify-content-center align-items-center m-5 p-5">
                    <b-spinner variant="info" role="status" />
                </div>
                <div v-else-if="candidateLoaded && data.candidateStatus?.isCreateUser" class="card shadow-sm mb-3">
                    <div v-if="data.user || userCreated" class="card-body text-center py-5">
                        <i class="mdi mdi-checkbox-marked-circle text-success display-4"></i>
                        <h4 class="font-weight-bold mt-3">Employee Account Created</h4>
                        <p class="text-muted mb-0">An employee account has already been created for this candidate.</p>
                    </div>
                    <form v-else @submit.prevent="createConfirmation">
                        <div class="card-body p-4">
                            <div class="d-flex align-items-center border-bottom pb-4 mb-4">
                                <div class="avatar-md mr-3 flex-shrink-0">
                                    <span class="avatar-title bg-soft-info text-info rounded-circle">
                                        <i class="mdi mdi-account-check-outline font-size-24"></i>
                                    </span>
                                </div>
                                <div>
                                    <small class="text-muted text-uppercase">Employee Onboarding</small>
                                    <h3 class="font-weight-bold mb-1">Employee Onboarding</h3>
                                </div>
                            </div>
                            <p class="font-weight-bold mb-2">Dear IT Department,</p>
                            <p class="text-muted mb-4">The candidate below has been hired. Please create their company email account using the details provided.</p>
                            <div class="border rounded mb-4 overflow-hidden">
                                <div class="bg-light px-4 py-3 border-bottom">
                                    <h5 class="font-weight-bold mb-1 text-break">{{ data.firstName }} {{ data.lastName }}</h5>
                                    <span class="text-muted">{{ data.candidateJob?.name || '--' }}</span>
                                </div>
                                <div class="px-4 pt-3 pb-1">
                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1">First Name</small>
                                            <span class="font-weight-medium text-break">{{ data.firstName || '--' }}</span>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1">Last Name</small>
                                            <span class="font-weight-medium text-break">{{ data.lastName || '--' }}</span>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1"><i class="mdi mdi-email-outline mr-1"></i>Personal Email</small>
                                            <span class="font-weight-medium text-break">{{ data.email || '--' }}</span>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1"><i class="mdi mdi-phone-outline mr-1"></i>Phone</small>
                                            <span class="font-weight-medium text-break">{{ data.phoneNumber ? [data.phoneCode, data.phoneNumber].filter(Boolean).join(' ') : '--' }}</span>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1"><i class="mdi mdi-domain mr-1"></i>Company</small>
                                            <span class="font-weight-medium text-break"><CompanyLink :company="data.candidateJob?.company" fallback="--" /></span>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <small class="text-muted d-block mb-1"><i class="mdi mdi-account-group-outline mr-1"></i>Department</small>
                                            <span class="font-weight-medium text-break">{{ data.candidateJob?.department?.name || '--' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <h5 class="font-weight-bold mb-2">
                                <i class="mdi mdi-email-plus-outline text-info mr-1"></i>
                                Company Email Account
                            </h5>
                            <p class="text-muted small mb-3">Enter the company email assigned to this employee to create their account.</p>
                            <b-form-group
                                label="Company Email"
                                label-cols="3"
                                label-align="left"
                            >
                                <b-form-input
                                    v-model.trim="createForm.companyEmail"
                                    :class="{ 'is-invalid': submitted && $v.createForm.companyEmail.$error }"
                                    autocomplete="off"
                                />
                                <b-form-invalid-feedback v-if="submitted && $v.createForm.companyEmail.$error" class="text-right">
                                    <span v-if="!$v.createForm.companyEmail.required">{{ $t('message.requiredX', ['Company Email']) }}</span>
                                    <span v-else-if="!$v.createForm.companyEmail.email">{{ $t('message.invalidX', ['Company Email']) }}</span>
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <div class="d-flex justify-content-end">
                                <b-button
                                    type="submit"
                                    variant="info"
                                    class="px-4 col-md-3"
                                    :disabled="!status.save"
                                >
                                    <div v-if="status.save">
                                        <i class="mdi mdi-send mr-1"></i><span>Submit</span>
                                    </div>
                                    <b-spinner v-else small />
                                </b-button>
                            </div>
                        </div>
                    </form>
                </div>
                <div v-else class="card shadow-sm">
                    <div class="card-body text-center py-5">
                        <b-alert
                            show
                            variant="warning"
                            class="my-0 text-center"
                        >
                            <i class="mdi mdi-alert-circle-outline"></i> <p class="text-muted mb-0">Candidate information is unavailable. Please open a valid candidate link.</p>
                        </b-alert>
                    </div>
                </div>
            </div>
        </div>
        <b-modal
            id="candidate-employee-confirmation"
            title="Confirm employee account"
            hide-header-close
            centered
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
        >
            <p>Please confirm the details below before creating this employee account.</p>
            <div class="bg-light rounded p-3">
                <small class="text-muted d-block">Candidate</small>
                <div class="font-weight-bold mb-3 text-break">{{ data.firstName }} {{ data.lastName }}</div>
                <small class="text-muted d-block">Company Email</small>
                <div class="font-weight-bold text-break">{{ createForm.companyEmail }}</div>
            </div>
            <template #modal-footer>
                <b-button variant="danger" class="col-md-2" :disabled="!status.save" @click="$bvModal.hide('candidate-employee-confirmation')">
                    {{ $t('label.cancel') }}
                </b-button>
                <b-button variant="info" class="col-md-2" :disabled="!status.save" @click="requestCreate">
                    <span v-if="status.save">Submit</span>
                    <b-spinner v-else small />
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { candidateMethods } from "@/state/public";
import { CANDIDATE_MODULE } from "@/state/public/candidate.state";

export default {
    page: {
        title: i18n.t("label.application"),
    },
    props: {
        candidateUUID: {
            type: String,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                companyEmail: { required, email },
            },
        };
    },
    async created() {
        await this.getCandidateData();
    },
    data() {
        return {
            submitted: false,
            candidateLoaded: false,
            userCreated: false,
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...candidateMethods,
        defaultForm() {
            return {
                uuid: null,
                companyEmail: "",
            };
        },
        async getCandidateData() {
            if (this.candidateUUIDValue) {
                await this.getCandidate({ uuid: this.candidateUUIDValue })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                companyEmail: "",
                            };
                            this.candidateLoaded = true;
                        }
                    });
            }
        },
        createConfirmation() {
            if (!this.status.save || !this.candidateLoaded || !this.data.candidateStatus?.isCreateUser || this.data.user || this.userCreated) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            this.$bvModal.show('candidate-employee-confirmation');
        },
        requestCreate() {
            if (!this.status.save || !this.candidateLoaded || !this.data.candidateStatus?.isCreateUser || this.data.user || this.userCreated) {
                return;
            }
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                };
                this.createCandidateUser({data})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.$bvModal.hide('candidate-employee-confirmation');
                            this.userCreated = true;
                            this.resetForm();
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(CANDIDATE_MODULE, ['status']),
        candidateUUIDValue() {
            return this.$route.query.candidate || this.candidateUUID;
        },
        data() {
            return this.$store.state.public.candidate.details;
        },
    },
}
</script>
